import { AspettoPill } from '@/components/molecules';
import { dwrClassesById } from '@/data/classes';
import type { AspettoCategoria } from '@/schemas/aspetti';
import type { ChosenAspetto } from '@/schemas/character';
import { cn } from '@/lib/utils';
import { normalizeOptionalTextInput } from '@/utils/textInput';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';

/**
 * Step 6 — Aspetti.
 *
 * Re-design Fase 2 PR 5: cambio paradigma UX da Select dropdown → pill-toggle
 * inline (decisione D1 del WIP). Per ogni categoria mostro le opzioni come
 * <AspettoPill> cliccabili + 1 input custom inline accanto. Counter
 * "{filled}/{total} selezionate" mostrato in cima al body.
 *
 * `ChosenAspetto.valore` accetta qualunque stringa fino a 150 caratteri,
 * quindi nessuna modifica schema serve per il custom.
 */
export function SectionAspetti() {
  const draft = useBuilderDraft();
  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;

  if (!classDef) {
    return (
      <p className="text-ink-soft italic">
        Seleziona prima una classe nello step precedente per vedere gli aspetti disponibili.
      </p>
    );
  }

  const total = classDef.aspetti.length;
  const filled = classDef.aspetti.filter((cat) =>
    draft.chosenAspetti.some((a) => a.categoria === cat.label),
  ).length;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-soft text-sm italic">
        Scegli un'opzione per ognuna delle {total} categorie, oppure scrivine una tua.{' '}
        <strong className="text-ink not-italic">
          {filled}/{total}
        </strong>{' '}
        selezionate.
      </p>

      <div className="border-ink bg-paper-bright px-5 py-4 outline outline-offset-[3px]">
        {classDef.aspetti.map((cat) => (
          <AspettoCategoryControl key={cat.label} category={cat} />
        ))}
      </div>
    </div>
  );
}

function AspettoCategoryControl({ category }: { category: AspettoCategoria }) {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  const currentValue = draft.chosenAspetti.find((a) => a.categoria === category.label)?.valore;
  const isCustom = currentValue !== undefined && !category.options.includes(currentValue);
  const customText = isCustom ? (currentValue ?? '') : '';

  function commit(value: string | undefined) {
    const without = draft.chosenAspetti.filter((a) => a.categoria !== category.label);
    // BugFix 2026-05-23: salva il valore RAW (no trim) per preservare gli
    // spazi durante typing. `normalizeOptionalTextInput` ritorna `null` solo
    // se il campo è effettivamente vuoto (whitespace-only) → in tal caso
    // rimuoviamo l'aspetto dal draft. Vedi `utils/textInput.ts` per dettagli.
    const normalized = value === undefined ? null : normalizeOptionalTextInput(value);
    const next: ChosenAspetto[] =
      normalized === null
        ? without
        : [...without, { categoria: category.label, valore: normalized }];
    dispatch({ type: 'SET_ASPETTI', chosenAspetti: next });
  }

  function pickPreset(option: string) {
    if (currentValue === option) commit(undefined);
    else commit(option);
  }

  return (
    <div className="border-rule grid grid-cols-[130px_1fr] items-start gap-3 border-b border-dotted py-[0.7em] last:border-b-0">
      <div className="font-display pt-[0.4em] text-[0.85rem] font-bold tracking-[0.06em] uppercase">
        {category.label}
      </div>
      <div className="flex flex-wrap items-center gap-[0.45rem]">
        {category.options.map((option) => (
          <AspettoPill
            key={option}
            label={option}
            selected={currentValue === option}
            onClick={() => pickPreset(option)}
          />
        ))}
        <CustomInput
          categoryLabel={category.label}
          value={customText}
          isActive={isCustom}
          onChange={(v) => commit(v)}
        />
      </div>
    </div>
  );
}

interface CustomInputProps {
  categoryLabel: string;
  value: string;
  isActive: boolean;
  onChange: (v: string) => void;
}

// page-specific, not reusable: input custom inline pattern .aspetti-custom del
// mockup. Verrà generalizzato come <Field variant="inline"> quando emergerà
// in PR 6+ (3 istanze totali con bond-blank e simili).
function CustomInput({ categoryLabel, value, isActive, onChange }: CustomInputProps) {
  return (
    <span
      className={cn(
        'ml-[0.2rem] inline-flex items-center',
        isActive &&
          "before:text-ink-soft before:mr-[0.3rem] before:text-[0.85rem] before:content-['✎']",
      )}
    >
      <input
        type="text"
        placeholder="…o scrivine uno tuo"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`Aspetto custom per ${categoryLabel}`}
        maxLength={150}
        className={cn(
          'font-serif placeholder:text-ink-faint text-ink min-w-[14ch] border-0 border-b-[1.5px] bg-transparent px-[0.2em] py-[0.3em] text-[0.88rem] italic outline-none placeholder:italic',
          'focus:border-accent-dwr focus:border-solid',
          isActive ? 'border-ink border-solid not-italic' : 'border-ink-faint border-dotted',
        )}
      />
    </span>
  );
}
